/**
 * Der Produkt-Papierkorb.
 *
 * WARUM KEINE EIGENE HAUPTNAVIGATION
 *
 * Ein Papierkorb ist kein Arbeitsbereich, sondern ein Zustand von Produkten.
 * Ein eigener Eintrag in der Hauptleiste haette ihn zu einem Ort gemacht, an
 * dem man arbeitet -- und er stuende dauerhaft neben „Produkte", „Modelle"
 * und „Familien", obwohl er die meiste Zeit leer ist. Er lebt deshalb IN den
 * Produkten, erreichbar aus der Produktliste.
 *
 * ZWEI HANDLUNGEN, ZWEI SCHRANKEN
 *
 * Wiederherstellen ist umkehrbar und kostet einen Klick. Endgueltig loeschen
 * ist es nicht: es verlangt eine andere Berechtigung, zeigt zuerst den
 * Befund, und laesst sich erst ausloesen, wenn die Artikelnummer eingetippt
 * ist. Das ist keine Schikane -- es ist die letzte Stelle, an der ein
 * Versehen noch auffallen kann.
 */
import { useCallback, useEffect, useState } from "react";
import type { ArticleTrashEntry, DeletePreflight } from "@homebase/shared";
import { api } from "../lib/api.ts";
import { useI18n } from "../i18n/index.tsx";
import { errorText } from "../i18n/errors.ts";
import { ErrorState } from "./States.tsx";
import "./ProductTrash.css";

function tag(iso: string): string {
  return iso.slice(0, 10).split("-").reverse().join(".");
}

export function ProductTrash({ onBack, mayWrite, mayDelete, onChanged }: {
  onBack: () => void;
  /** Wiederherstellen -- `article.write`. */
  mayWrite: boolean;
  /** Endgueltig loeschen -- `article.delete`. Heute nur der Administrator. */
  mayDelete: boolean;
  /** Der Bestand hat sich geaendert; die Produktliste muss neu laden. */
  onChanged: () => void;
}): React.JSX.Element {
  const i18n = useI18n();
  const t = i18n.t;
  const [items, setItems] = useState<readonly ArticleTrashEntry[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<number | null>(null);
  /** Der offene Loeschbefund -- immer nur einer. */
  const [befund, setBefund] = useState<DeletePreflight | null>(null);
  const [bestaetigung, setBestaetigung] = useState("");

  const laden = useCallback(async () => {
    try { setItems((await api.trash()).items); setError(null); }
    catch (e) { setError(e); }
  }, []);
  useEffect(() => { void laden(); }, [laden]);

  async function wiederherstellen(e: ArticleTrashEntry): Promise<void> {
    setBusy(e.id);
    try {
      await api.restoreArticle(e.id);
      await laden(); onChanged(); setError(null);
    } catch (err) { setError(err); } finally { setBusy(null); }
  }

  async function befundHolen(e: ArticleTrashEntry): Promise<void> {
    setBusy(e.id); setBestaetigung("");
    try { setBefund(await api.deletePreflight(e.id)); setError(null); }
    catch (err) { setError(err); } finally { setBusy(null); }
  }

  async function endgueltig(): Promise<void> {
    if (!befund) return;
    setBusy(befund.articleId);
    try {
      await api.purgeArticle(befund.articleId, { confirmArticleNumber: bestaetigung });
      setBefund(null); setBestaetigung("");
      await laden(); onChanged(); setError(null);
    } catch (err) { setError(err); } finally { setBusy(null); }
  }

  return (
    <div className="trash">
      <header className="trash__head">
        <button className="btn" onClick={onBack}>{t("trash.back")}</button>
        <div>
          <h2 className="trash__title">{t("trash.title")}</h2>
          <p className="trash__sub">{t("trash.intro")}</p>
        </div>
      </header>

      {error !== null && <ErrorState message={errorText(i18n, error)} />}

      {items !== null && items.length === 0 && (
        <p className="trash__empty">{t("trash.empty")}</p>
      )}

      {items !== null && items.length > 0 && (
        <table className="trash__table">
          <thead>
            <tr>
              <th>{t("trash.number")}</th>
              <th>{t("trash.description")}</th>
              <th>{t("trash.family")}</th>
              <th>{t("trash.deletedAt")}</th>
              <th>{t("trash.deletedBy")}</th>
              <th>{t("trash.reason")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id}>
                <td className="trash__num">{e.articleNumber}</td>
                {/* Eine fehlende Bezeichnung wird benannt, nicht verschwiegen --
                    sie ist oft genau der Grund, warum der Artikel hier liegt. */}
                <td>{e.shortDescription ?? <span className="trash__none">{t("trash.noDescription")}</span>}</td>
                <td>{e.familyName ?? "—"}</td>
                <td>{tag(e.deletedAt)}</td>
                <td>{e.deletedBy ?? "—"}</td>
                <td>
                  {/* Der Code ist die Wahrheit, die Beschriftung kommt aus dem
                      Woerterbuch -- wie bei jedem Wertevorrat. `reasonName`
                      aus der Datenbank ist nur der Rueckfall. */}
                  {i18n.tCode("deletionReason", e.reasonCode, e.reasonName)}
                  {e.note !== null && <span className="trash__note"> — {e.note}</span>}
                </td>
                <td className="trash__actions">
                  {mayWrite && (
                    <button className="btn" disabled={busy === e.id}
                      onClick={() => void wiederherstellen(e)}>{t("trash.restore")}</button>
                  )}
                  {mayDelete && (
                    <button className="btn btn--danger" disabled={busy === e.id}
                      onClick={() => void befundHolen(e)}>{t("trash.purgeOpen")}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {befund !== null && (
        <div className="trash__preflight" role="dialog" aria-label={t("trash.preflightTitle")}>
          <h3>{t("trash.preflightTitle", { number: befund.articleNumber })}</h3>
          {/*
            * DER BEFUND ZEIGT ALLE REFERENZARTEN, AUCH DIE MIT NULL.
            *
            * Eine Liste, die nur zeigt, was gefunden wurde, laesst offen, ob
            * ueberhaupt gesucht wurde. Hier haengt eine unumkehrbare
            * Entscheidung daran.
            */}
          <table className="trash__refs">
            <tbody>
              {befund.references.map((r) => (
                <tr key={r.table} className={r.count > 0 ? "trash__ref--hit" : undefined}>
                  <td>{r.label}</td>
                  <td className="trash__refcount">{r.count}</td>
                  <td className="trash__refkind">{t(`trashKind.${r.kind}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className={`trash__status trash__status--${befund.status}`}>
            {t(`trashStatus.${befund.status}`)}
          </p>
          <ul className="trash__reasons">
            {befund.reasons.map((g, i) => <li key={i}>{g}</li>)}
          </ul>

          {befund.status === "safe" ? (
            <div className="trash__confirm">
              {/* Die zweite, staerkere Bestaetigung: die Nummer muss getippt
                  werden. Ein „Sind Sie sicher?" beantwortet man im Reflex. */}
              <label htmlFor="trash-confirm">
                {t("trash.confirmLabel", { number: befund.articleNumber })}
              </label>
              <input id="trash-confirm" className="trash__input" value={bestaetigung}
                autoComplete="off"
                onChange={(ev) => setBestaetigung(ev.target.value)} />
              <button className="btn btn--danger"
                disabled={bestaetigung !== befund.articleNumber || busy !== null}
                onClick={() => void endgueltig()}>{t("trash.purgeConfirm")}</button>
            </div>
          ) : (
            <p className="trash__blocked">{t("trash.blockedHint")}</p>
          )}
          <button className="btn" onClick={() => { setBefund(null); setBestaetigung(""); }}>
            {t("trash.close")}
          </button>
        </div>
      )}
    </div>
  );
}
